import React, { useState, useCallback, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import Icon from 'components/Icon'
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
} from 'recharts'
import { Button } from '@arnsbogroup/react-voicearchive'

const Chart = ({ ranges, currency, onChange, readOnly, currencySymbol = "€" }) => {
  const { formatMessage } = useIntl()
  const chartRef = useRef(null)
  const [state, setState] = useState({ x: 0, y: 0, rate: null })
  const [max, setMax] = useState(7000)

  const data = useMemo(() => {
    let points = []

    ranges.filter(({ to }) => max ? to <= max : true).map((props, i) => {
      let { amount, from, to, type } = props

      if (type == 'ABSOLUTE') {
        points.push({ amount: from, value: amount })
        points.push({ amount: to, value: amount })
      } else {
        const last = points[points.length - 1]

        if ((amount - 1) !== last.amount) {
          points.push({ amount: from, value: ((from - last.amount) * amount) + last.value })
        }

        points.push({ amount: to, value: ((to - last.amount) * amount) + last.value })
      }
    })

    return points
  }, [ranges, max])

  const onMouseLeave = () => setState({ x: 0, y: 0, rate: null })

  const onMouseMove = useCallback((event) => {
    const { current: { state: { xAxisMap: { 0: { scale: { invert: scale } } } } } } = chartRef

    if (event && event.chartX && event.chartY) {
      let xPoint = scale(event.chartX)
      let nextRate = 0

      if (xPoint > data[data.length - 1].amount) {
        xPoint = data[data.length - 1].amount
      }

      ranges.map(({ amount, from, to, type }, i) => {
        if (type == 'ABSOLUTE' && xPoint > from) {
          nextRate = amount
        } else if (type == 'RELATIVE' && xPoint > from) {
          let delta = xPoint - (from - 1)

          if (delta > (to - from)) {
            delta = to - from
          }

          nextRate = nextRate + (delta * amount)
        }
      })

      setState({
        x: xPoint.toFixed(0),
        y: event.chartY,
        rate: nextRate.toFixed(2)
      })
    }
  })

  const onClick = (abs, rel) => {
    const rangeIndex = ranges
      .findIndex(({ amount, from, to, type }, i) => (
        state.x >= from && state.x <= to
      ))

    const range = ranges[rangeIndex]

    if (range.type == 'ABSOLUTE') {
      onChange(rangeIndex, ((range.amount * 1000) + abs) / 1000)
    } else {
      onChange(rangeIndex, ((range.amount * 1000) + rel) / 1000)
    }
  }

  const CustomLabel = (props) => (
    <foreignObject
      className="overflow-visible"
      x={props.viewBox.x - 8}
      y={(chartRef.current.container.offsetHeight / 2) - 20}
      width={16}
      height={50}
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        className="w-4 h-10 flex flex-col items-center justify-between text-sm p-1 shadow text-sm text-gray-400"
        style={{ backgroundColor: '#e2e8f0' }}
      >
        <Icon
          icon="arrow-up"
          className="block hover:text-gray-700 cursor-pointer"
          onClick={() => onClick(5000, 10)}
          title={formatMessage(messages.increaseRateTitle, {currencySymbol: props.currencySymbol})}
        />

        <Icon
          icon="arrow-down"
          className="block hover:text-gray-700 cursor-pointer"
          onClick={() => onClick(-5000, -10)}
          title={formatMessage(messages.decreaseRateTitle, {currencySymbol: props.currencySymbol})}
        />
      </div>
    </foreignObject>
  )

  return (
    <div>
      <div className="flex justify-between">
        <div className="text-sm text-gray-500">
          { state.rate ? (
            <FormattedMessage {...messages.chartHoverHint } values={{ words: state.x, amount: state.rate, currency }} />
          ) : (
            <FormattedMessage {...messages.chartHint } />
          ) }
        </div>

        { max &&
          <Button
            variant="inline"
            size='xs'
            className="border-b text-gray-400 border-gray-400 border-dotted" 
            onClick={() => setMax(null)}
          >
            <FormattedMessage {...messages.chartShowMore } values={{ words: data[data.length - 1].amount }} />
          </Button>
        }
      </div>
      <div className="flex">
        <div className="flex items-center justify-center text-sm text-gray-500">
          <span>{currencySymbol}</span>
        </div>
        <ResponsiveContainer width="100%" height={120}>
          <LineChart ref={chartRef} data={data} onMouseDown={onMouseMove} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
            <Line
              stroke="#38a169"
              dataKey="value"
              type="linear"
              dot={{
                stroke: '#38a169',
                strokeWidth: 1,
              }}
            />

            <XAxis dataKey="amount" type="number" hide />

            { state.rate &&
              <ReferenceLine x={state.x} stroke="#e2e8f0" label={readOnly ? undefined : CustomLabel} />
            }
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="w-full flex justify-between text-sm text-gray-500">
        <span>{ data[0].amount }</span>
        <span>
          <FormattedMessage {...messages.wordCount} />
        </span>
        <span>{ data[data.length - 1].amount }</span>
      </div>
    </div>
  )
}

export default Chart
